import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { APP_CONFIG, AppConfig } from '../../app.config';
import { CORRELATION_ID, generateCorrelationId } from '../tracking/tracking-tokens';

/** Identifies Policy360 as the origin of the session to the auth service and the portal. */
export const PORTLET_SOURCE_SYSTEM = 'Policy360';

/** Portlet each link opens; matches the portletType the portal derives from its route. */
export const PORTLET_TYPE = {
    billing: 'Billing Portlet',
    documentUpload: 'Document Portlet',
    snl: 'SNL Portlet',
} as const;

export interface PortletSessionRequest {
    policyNumber: string;
    propertyZipCode: string;
    streetNumber?: string;
    phoneNumber?: string;
    /** Generated when not supplied so every session request carries one. */
    correlationId?: string;
    /** Logged by the auth service with the session; set by the get*Link helpers. */
    portletType?: string;
}

export interface PortletSessionResponse {
    sessionId: string;
}

@Injectable({ providedIn: 'root' })
export class PortletService {
    constructor(private http: HttpClient, @Inject(APP_CONFIG) private config: AppConfig) {}

    /**
     * Calls the auth session endpoint and returns the raw sessionId.
     * The correlationId is sent in the body and stamped on the request context
     * so the logging interceptor records the api_call under the same id.
     */
    createSession(request: PortletSessionRequest): Observable<string> {
        const correlationId = request.correlationId || generateCorrelationId();
        const body = {
            policyNumber: request.policyNumber,
            propertyZipCode: request.propertyZipCode,
            streetNumber: request.streetNumber ?? '',
            phoneNumber: request.phoneNumber ?? '',
            correlationId,
            sourceSystem: PORTLET_SOURCE_SYSTEM,
            ...(request.portletType && { portletType: request.portletType }),
        };
        const headers = { 'x-api-key': this.config.authSessionApiKey };
        const context = new HttpContext().set(CORRELATION_ID, correlationId);

        return this.http.post<PortletSessionResponse>(this.config.authSessionUrl, body, { headers, context }).pipe(
            map((response) => {
                const sessionId = response?.sessionId;
                if (!sessionId) {
                    throw new Error('Missing sessionId in auth session response.');
                }
                return sessionId;
            }),
            catchError((error) => throwError(() => error))
        );
    }

    /**
     * Builds the full portlet link with sessionId, correlationId and sourceSystem query params.
     */
    buildPortletLink(sessionId: string, correlationId: string): string {
        return this.appendSessionParams(this.config.portletUrl, sessionId, correlationId);
    }

    /**
     * Builds the full document upload link with sessionId, correlationId and sourceSystem query params.
     */
    buildDocumentUploadLink(sessionId: string, correlationId: string): string {
        const base = this.config.documentUploadUrl;
        if (!base) {
            throw new Error('documentUploadUrl is not configured. Please restart the application.');
        }
        return this.appendSessionParams(base, sessionId, correlationId);
    }

    /**
     * Builds the full SNL link with sessionId, correlationId and sourceSystem query params.
     */
    buildSNLLink(sessionId: string, correlationId: string): string {
        const base = this.config.snlUrl;
        if (!base) {
            throw new Error('snlUrl is not configured. Please restart the application.');
        }
        return this.appendSessionParams(base, sessionId, correlationId);
    }

    /**
     * Convenience: creates a session and returns the ready-to-open portlet URL.
     */
    getPortletLink(request: PortletSessionRequest): Observable<string> {
        return this.createSessionLink(request, PORTLET_TYPE.billing, (sessionId, correlationId) =>
            this.buildPortletLink(sessionId, correlationId)
        );
    }

    /**
     * Convenience: creates a session and returns the ready-to-use document upload URL.
     */
    getDocumentUploadLink(request: PortletSessionRequest): Observable<string> {
        return this.createSessionLink(request, PORTLET_TYPE.documentUpload, (sessionId, correlationId) =>
            this.buildDocumentUploadLink(sessionId, correlationId)
        );
    }

    /**
     * Convenience: creates a session and returns the ready-to-use SNL URL.
     */
    getSNLLink(request: PortletSessionRequest): Observable<string> {
        return this.createSessionLink(request, PORTLET_TYPE.snl, (sessionId, correlationId) =>
            this.buildSNLLink(sessionId, correlationId)
        );
    }

    /**
     * Creates a session and builds a link carrying the same correlationId that was
     * sent to auth/session, so the portal's /auth/login continues the same trace.
     */
    private createSessionLink(
        request: PortletSessionRequest,
        portletType: string,
        buildLink: (sessionId: string, correlationId: string) => string
    ): Observable<string> {
        const correlationId = request.correlationId || generateCorrelationId();
        return this.createSession({ ...request, correlationId, portletType }).pipe(
            map((sessionId) => buildLink(sessionId, correlationId))
        );
    }

    private appendSessionParams(base: string, sessionId: string, correlationId: string): string {
        const separator = base.includes('?') ? '&' : '?';
        const params = new URLSearchParams({ sessionId, correlationId, sourceSystem: PORTLET_SOURCE_SYSTEM });
        return `${base}${separator}${params.toString()}`;
    }
}
